import { ReactNode, useEffect, useState } from 'react'
import { Button, Fa, FormItem, TextInput } from '@eltoro-ui/components'
import { Orgmanagerv1Contact } from 'next-gen-sdk'
import classNames from 'classnames'
import { ErrorMessage, isValidCharLength, validCheck } from 'Helpers'
import { SelectCountry, SelectState } from 'Components'
import { useValidation } from '@eltoro-ui/hooks'

export const ContactForm: React.FC<{
  contact: Orgmanagerv1Contact
  onChange: (newContact: Orgmanagerv1Contact) => void
  isValidEmail: boolean
  isValidPhone: boolean
  isValidZip: boolean
  className?: string
  contactHeader?: ReactNode
  addressHeader?: ReactNode
  showSecondaryEmails?: boolean
}> = ({
  contact,
  className,
  onChange,
  isValidEmail,
  isValidPhone,
  isValidZip,
  contactHeader,
  addressHeader,
  showSecondaryEmails,
}) => {
  const [fieldsTouched, setFieldsTouched] = useState<string[]>([])
  const validateSecondaryEmail = useValidation('email')

  const {
    firstName,
    lastName,
    phone,
    email,
    address,
    address2,
    city,
    state,
    zip,
    country,
    secondaryEmails,
  } = contact

  const handleBlur = (field: string) => {
    setFieldsTouched((prev) => [...new Set([...prev, field])])
  }

  const isTouched = (field: string) =>
    fieldsTouched.some((existing) => existing === field)

  return (
    <div className={`ContactForm flex flex-col gap-4 pt-4 ${className}`}>
      {contactHeader}
      <FormItem
        htmlFor={`first-name-${contact.id}`}
        label="First Name:"
        required
        errorMessage={ErrorMessage({
          fieldName: firstName || '',
          max: 100,
          label: 'First name',
        })}
        valid={
          validCheck(firstName || '') && isValidCharLength(firstName || '', 100)
        }
      >
        <TextInput
          valid={isValidCharLength(firstName || '', 100)}
          hideValidIcon
          maxLength={101}
          value={firstName || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, firstName: e.target.value })
          }
          onBlur={() => handleBlur('firstName')}
          autoComplete="given-name"
        />
      </FormItem>
      <FormItem
        htmlFor={`last-name-${contact.id}`}
        label="Last name:"
        errorMessage={ErrorMessage({
          fieldName: lastName || '',
          max: 100,
          label: 'Last name',
        })}
        valid={
          validCheck(lastName || '') && isValidCharLength(lastName || '', 100)
        }
        required
      >
        <TextInput
          valid={isValidCharLength(lastName || '', 100)}
          hideValidIcon
          value={lastName || ''}
          maxLength={101}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, lastName: e.target.value })
          }
          onBlur={() => handleBlur('lastName')}
          autoComplete="family-name"
        />
      </FormItem>
      <FormItem
        htmlFor={`phone-${contact.id}`}
        label="Phone:"
        required
        valid={
          !isTouched('phone')
            ? isValidPhone
            : isValidPhone && (phone || '').length > 1
        }
        errorMessage={ErrorMessage({
          fieldName: phone || '',
          max: 10,
          label: 'Phone number',
          isValid: isValidPhone,
        })}
        counter={(phone || '').length > 0}
      >
        <TextInput
          valid={isValidPhone && (phone || '').length > 1}
          hideValidIcon
          value={phone || ''}
          maxLength={11}
          // safari handles number as alphanumerical field change to tel so the FormItem's handler will prevent alpha chars.
          type="tel"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, phone: e.target.value })
          }
          onBlur={() => handleBlur('phone')}
          autoComplete="tel-national"
        />
      </FormItem>
      <FormItem
        htmlFor={`email-${contact.id}`}
        label="Email:"
        required
        valid={
          !isTouched('email')
            ? isValidEmail
            : isValidEmail && (email || '').length > 1
        }
        errorMessage={ErrorMessage({
          fieldName: email || '',
          max: 255,
          label: 'Email Address',
          isValid: isValidEmail,
        })}
      >
        <TextInput
          valid={isValidEmail && (email || '').length > 1}
          hideValidIcon
          maxLength={256}
          value={email || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, email: e.target.value })
          }
          onBlur={() => handleBlur('email')}
          autoComplete="email"
        />
      </FormItem>
      {/* secondary email addition */}
      {showSecondaryEmails && (
        <div className="flex flex-col gap-4">
          {secondaryEmails?.map((secondaryEmail, index) => (
            <FormItem
              key={index}
              htmlFor={`secondary-email-${index}-${contact.id}`}
              label={`Secondary Email ${index + 1}:`}
              errorMessage={ErrorMessage({
                fieldName: secondaryEmail || '',
                max: 255,
                label: 'Secondary Email Address',
              })}
              valid={
                validCheck(secondaryEmail || '') &&
                isValidCharLength(secondaryEmail || '', 255) &&
                validateSecondaryEmail(secondaryEmail || '')
              }
            >
              <div className="flex flex-row items-center gap-2">
                <TextInput
                  valid={isValidCharLength(secondaryEmail || '', 255)}
                  hideValidIcon
                  maxLength={256}
                  value={secondaryEmail || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newSecondaryEmails = [...secondaryEmails]
                    newSecondaryEmails[index] = e.target.value
                    onChange({
                      ...contact,
                      secondaryEmails: newSecondaryEmails,
                    })
                  }}
                  autoComplete="email"
                />
                <Button
                  onClick={() =>
                    onChange({
                      ...contact,
                      secondaryEmails: secondaryEmails?.filter(
                        (_, i) => i !== index,
                      ),
                    })
                  }
                  size="s"
                  iconOnly={<Fa icon="xmark" size={1} />}
                />
              </div>
            </FormItem>
          ))}
          <Button
            onClick={() =>
              onChange({
                ...contact,
                secondaryEmails: [...(secondaryEmails || []), ''],
              })
            }
            size="s"
            iconLeft={<Fa icon="plus" size={1} />}
            kind="text-icon"
          >
            Add Secondary Email
          </Button>
        </div>
      )}
      {addressHeader}
      <FormItem
        htmlFor={`address-${contact.id}`}
        label="Address:"
        errorMessage={ErrorMessage({
          fieldName: address || '',
          max: 255,
          label: 'Address',
        })}
        valid={
          validCheck(address || '') && isValidCharLength(address || '', 255)
        }
        required
      >
        <TextInput
          valid={isValidCharLength(address || '', 255)}
          hideValidIcon
          maxLength={256}
          value={address || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, address: e.target.value })
          }
          onBlur={() => handleBlur('address')}
          autoComplete="address-line1"
        />
      </FormItem>
      <FormItem
        htmlFor={`address2-${contact.id}`}
        label="Address 2:"
        errorMessage={ErrorMessage({
          fieldName: address2 || '',
          max: 255,
          label: 'Address 2',
        })}
        valid={
          validCheck(address2 || '') && isValidCharLength(address2 || '', 255)
        }
      >
        <TextInput
          valid={isValidCharLength(address2 || '', 255)}
          hideValidIcon
          maxLength={256}
          value={address2 || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, address2: e.target.value })
          }
          onBlur={() => handleBlur('address2')}
          autoComplete="address-line2"
        />
      </FormItem>
      <FormItem
        htmlFor={`city-${contact.id}`}
        label="City:"
        errorMessage={ErrorMessage({
          fieldName: city || '',
          max: 255,
          label: 'City name',
        })}
        valid={validCheck(city || '') && isValidCharLength(city || '', 255)}
        required
      >
        <TextInput
          valid={isValidCharLength(city || '', 255)}
          hideValidIcon
          maxLength={256}
          value={city || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, city: e.target.value })
          }
          onBlur={() => handleBlur('city')}
          autoComplete="home city"
        />
      </FormItem>
      {country === 'United States of America' && (
        <FormItem htmlFor={`state-${contact.id}`} label="State:" required>
          <SelectState
            country={country}
            value={state}
            onChange={(state) => onChange({ ...contact, state })}
            className={classNames(
              'ContactForm__SelectState border-tint-gray-300 min-h-[2.5rem] pb-1',
              {
                '!border-warning animate-glowwarning':
                  isTouched('state') && (state || '').length < 2,
                'border-success animate-glowsuccess':
                  isTouched('state') && state && state.length > 1,
              },
            )}
            onBlur={() => handleBlur('state')}
          />
        </FormItem>
      )}
      {country === 'Canada' && (
        <FormItem htmlFor={`state-${contact.id}`} label="Province:" required>
          <SelectState
            country={country}
            value={state}
            onChange={(state) => onChange({ ...contact, state })}
            className={classNames(
              'ContactForm__SelectState border-tint-gray-300 min-h-[2.5rem] pb-1',
              {
                '!border-warning animate-glowwarning':
                  isTouched('state') && (state || '').length < 2,
                'border-success animate-glowsuccess':
                  isTouched('state') && state && state.length > 1,
              },
            )}
            onBlur={() => handleBlur('state')}
          />
        </FormItem>
      )}
      {country === 'Puerto Rico' && (
        <FormItem
          htmlFor={`state-${contact.id}`}
          label="Municipality:"
          required
        >
          <SelectState
            country={country}
            value={state}
            onChange={(state) => onChange({ ...contact, state })}
            className={classNames(
              'ContactForm__SelectState border-tint-gray-300 min-h-[2.5rem] pb-1',
              {
                '!border-warning animate-glowwarning':
                  isTouched('state') && (state || '').length < 2,
                'border-success animate-glowsuccess':
                  isTouched('state') && state && state.length > 1,
              },
            )}
            onBlur={() => handleBlur('state')}
          />
        </FormItem>
      )}
      <FormItem htmlFor={`country-${contact.id}`} label="Country:" required>
        <SelectCountry
          value={country}
          onChange={(country) => onChange({ ...contact, country })}
          className={classNames(
            'ContactForm__SelectCountry border-tint-gray-300 min-h-[2.5rem] pb-1',
            {
              '!border-warning animate-glowwarning':
                isTouched('country') && (country || '').length < 2,
              'border-success animate-glowsuccess':
                isTouched('country') && country && country.length > 1,
            },
          )}
          onBlur={() => handleBlur('country')}
        />
      </FormItem>
      <FormItem
        htmlFor={`zip-${contact.id}`}
        label={country === 'Canada' ? 'Postal Code:' : 'ZIP Code:'}
        required
        valid={
          !isTouched('zip') ? isValidZip : isValidZip && (zip || '').length > 1
        }
        errorMessage={ErrorMessage({
          fieldName: zip || '',
          max: 6,
          label: 'ZIP code',
          isValid: isValidZip,
        })}
        counter={(zip || '').length > 0}
      >
        <TextInput
          valid={isValidZip && (zip || '').length > 1}
          hideValidIcon
          value={zip || ''}
          maxLength={7}
          type="text"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...contact, zip: e.target.value })
          }
          onBlur={() => handleBlur('zip')}
          autoComplete="postal-code"
        />
      </FormItem>
    </div>
  )
}
